/**
 * Project Query Hooks - All Project API Calls with Caching
 *
 * CACHED:
 * - Dashboard Projects
 * - Hidden Projects
 * - GWP By Stage (Aggregated)
 * - GWP By Env Type (Aggregated)
 * - Project By ID
 * - Process Data
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Process } from "../db_access/process";
import { Project } from "../db_access/project";
import { CacheDebugger } from "./queryClient";

// -------------------------------------------------------------
// DASHBOARD HOOKS
// -------------------------------------------------------------

/**
 * Fetch all projects for my workspace (cached 2 min)
 */
export const useProjectsDashboard = () => {
  return useQuery({
    queryKey: ["projects", "dashboard"],
    queryFn: async () => {
      const res = await Project.get_from_my_workspace();
      const projects = await res.json();

      CacheDebugger.fetch("Dashboard Projects", projects.length);
      return projects;
    },
    staleTime: 0,
  });
};

/**
 * Fetch all projects for comparison dropdown (cached 5 min)
 */
export const useProjectsAll = () => {
  return useQuery({
    queryKey: ["projects", "all"],
    queryFn: async () => {
      const res = await Project.get_mine();
      const projects = await res.json();
      CacheDebugger.fetch("All Projects", projects.length);
      return projects;
    },
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch archived projects (cached 5 min)
 */
export const useProjectsArchive = () => {
  return useQuery({
    queryKey: ["projects", "archive"],
    queryFn: async () => {
      const res = await Project.get_my_company_archive();
      const projects = await res.json();
      CacheDebugger.fetch("Archived Projects", projects.length);
      return projects;
    },
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch hidden projects (cached 5 min)
 */
export const useHiddenProjects = () => {
  return useQuery({
    queryKey: ["projects", "hidden"],
    queryFn: async () => {
      const res = await Project.get_hidden();
      const data = await res.json();
      CacheDebugger.fetch("Hidden Projects", data.length);
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch total GWP by stage for projects (cached 5 min)
 */
export const useProjectsGwpByStage = (projectIds = []) => {
  const sortedIdsString = [...projectIds].sort().join(",");
  const queryKey = ["projects", "gwpByStage", sortedIdsString || "none"];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      const res = await Project.get_total_gwp_by_stage(projectIds);
      const data = await res.json();
      CacheDebugger.fetch("Projects GWP By Stage", projectIds.length);
      return data;
    },
    enabled: projectIds.length > 0,
    staleTime: 0,
  });
};

/**
 * Fetch total GWP by env type for projects (cached 5 min)
 */
export const useProjectsGwpByEnvType = (projectIds = []) => {
  const sortedIdsString = [...projectIds].sort().join(",");
  const queryKey = ["projects", "gwpByEnvType", sortedIdsString || "none"];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      const res = await Project.get_total_gwp_by_env_type(projectIds);
      const data = await res.json();
      CacheDebugger.fetch("Projects GWP By Env Type", projectIds.length);
      return data;
    },
    enabled: projectIds.length > 0,
    staleTime: 0,
  });
};

// -------------------------------------------------------------
// WORKSPACE HOOKS
// -------------------------------------------------------------

/**
 * Fetch project by ID (cached 5 min)
 */
export const useProjectById = (projectId) => {
  return useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      const res = await Project.get_by_id(projectId);
      const data = await res.json();
      CacheDebugger.fetch(`Project ${projectId}`);
      return data[0]; // Returns single object
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch process data for project (cached 10 min)
 */
export const useProjectProcessData = (projectId) => {
  return useQuery({
    queryKey: ["project", projectId, "process"],
    queryFn: async () => {
      const res = await Process.get_by_project_ids([projectId]);
      const data = await res.json();
      CacheDebugger.fetch(`Process Data (Project ${projectId})`);
      return data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 10,
  });
};

// -------------------------------------------------------------
// CACHE INVALIDATION HELPER
// -------------------------------------------------------------

export const useInvalidateProjectCache = () => {
  const queryClient = useQueryClient();

  return {
    invalidateProject: (projectId) => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
    invalidateDashboard: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    invalidateArchive: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", "archive"] });
    },
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ["project"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  };
};
